import { useState } from 'react'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import ListGroup from 'react-bootstrap/ListGroup'
import type { GroupMember, ReceiptItem } from '../../../../types'
import { formatAmount, formatMoney, parseAmount, toAmountInputValue } from '../../utils/money'
import { MemberPicker } from './MemberPicker'

export type ReceiptItemRowProps = {
    item: ReceiptItem
    members: GroupMember[]
    currencyCode: string
    exponent: number
    disabled?: boolean
    onSave: (changes: { label?: string; totalPrice?: number; memberIds?: string[] }) => Promise<void>
    onDelete: () => Promise<void>
}

export function ReceiptItemRow({ item, members, currencyCode, exponent, disabled, onSave, onDelete }: ReceiptItemRowProps) {
    const [editing, setEditing] = useState(false)
    const [label, setLabel] = useState(item.label)
    const [amount, setAmount] = useState(toAmountInputValue(item.totalPrice, exponent))
    const [saving, setSaving] = useState(false)

    const podilNaOsobu = item.memberIds.length > 0 ? Math.floor(item.totalPrice / item.memberIds.length) : item.totalPrice

    const ulozit = async () => {
        const totalPrice = parseAmount(amount, exponent)
        setSaving(true)
        try {
            await onSave({ label, totalPrice: totalPrice ?? item.totalPrice })
            setEditing(false)
        } finally {
            setSaving(false)
        }
    }

    return (
        <ListGroup.Item>
            {editing ? (
                <div className="d-flex flex-column gap-2">
                    <Form.Control
                        value={label}
                        aria-label={`Název položky ${item.label}`}
                        onChange={(event) => setLabel(event.target.value)}
                        className="su-tappable"
                    />
                    <div className="d-flex gap-2">
                        <Form.Control
                            value={amount}
                            inputMode="decimal"
                            aria-label={`Cena položky ${item.label}`}
                            className="text-end su-tappable"
                            onChange={(event) => setAmount(event.target.value)}
                        />
                        <Button variant="primary" className="su-tappable" disabled={saving} onClick={() => void ulozit()}>
                            Uložit
                        </Button>
                        <Button
                            variant="outline-secondary"
                            className="su-tappable"
                            disabled={saving}
                            onClick={() => {
                                setLabel(item.label)
                                setAmount(toAmountInputValue(item.totalPrice, exponent))
                                setEditing(false)
                            }}
                        >
                            Zrušit
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="d-flex align-items-start gap-2 mb-2">
                    <div className="flex-grow-1 min-w-0">
                        <div className="fw-semibold text-truncate">{item.label}</div>
                        {item.memberIds.length > 1 && (
                            <div className="small text-secondary">
                                {formatAmount(podilNaOsobu, exponent)} na osobu ({item.memberIds.length})
                            </div>
                        )}
                        {item.source !== 'MANUAL' && (
                            <div className="small text-secondary">
                                <i className="bi bi-magic me-1" aria-hidden="true" />
                                rozpoznáno ({item.source})
                            </div>
                        )}
                    </div>
                    <div className="fw-semibold text-nowrap">{formatMoney(item.totalPrice, currencyCode, exponent)}</div>
                    <Button
                        variant="link"
                        size="sm"
                        className="p-1 text-secondary su-tappable"
                        aria-label={`Upravit položku ${item.label}`}
                        disabled={disabled}
                        onClick={() => setEditing(true)}
                    >
                        <i className="bi bi-pencil" aria-hidden="true" />
                    </Button>
                    <Button
                        variant="link"
                        size="sm"
                        className="p-1 text-danger su-tappable"
                        aria-label={`Smazat položku ${item.label}`}
                        disabled={disabled}
                        onClick={() => void onDelete()}
                    >
                        <i className="bi bi-trash" aria-hidden="true" />
                    </Button>
                </div>
            )}

            <MemberPicker
                label={`Osoby u položky ${item.label}`}
                members={members}
                selected={item.memberIds}
                disabled={disabled}
                onChange={(memberIds) => void onSave({ memberIds })}
            />

            {item.memberIds.length === 0 && (
                <div className="small text-warning-emphasis mt-1">
                    <i className="bi bi-exclamation-triangle me-1" aria-hidden="true" />
                    Zatím nikomu nepřiřazeno
                </div>
            )}
        </ListGroup.Item>
    )
}
